import { Empty } from '@/components/common/empty';
import { UserItem } from '@/components/common/output/user';
import { useApiWithAuth } from '@/hooks/api';
import type {
  Comment,
  CommentSensitivity,
  ReactionType,
  ReactionsMap,
} from '@/shared/types/comment';
import {
  Button,
  Checkbox,
  CircularProgress,
  Spacer,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from '@heroui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCurrentUser, useUsersList } from '@/store/users/hooks';
import { StandardIcon } from '@/components/common/icon';
import {
  postComment as createPost,
  updateComment as updatePost,
  loadComments,
} from '@/store/people/actions/comments';
import { reactToComment as apiReactToComment } from '@/store/people/actions/comments';
import { useUsersLookup } from '@/components/access/users/hooks';
import { Editor } from '@/components/common/comments/editor';
import { Display } from '@/components/common/comments/display';
import type { SerializedEditorState, SerializedLexicalNode } from 'lexical';
import type { User } from '@/shared/types/user';
import type { SerializedBeautifulMentionNode } from 'lexical-beautiful-mentions';
import type { PeopleAccess } from '@/shared/types/policy';
import { useUserAccess, useUserId } from '@/hooks/auth';
import { useDismissMention } from '@/store/mentions/hooks';
import type { Person } from '@/store/people/types';

export function CommentsContent({
  subject,
  access,
  target,
  currentMention,
}: {
  subject: Person;
  access: PeopleAccess;
  target: string | null;
  currentMention?: string;
}) {
  const userId = useUserId();
  const userAccess = useUserAccess();
  const mentionUsers = useUsersList({});
  const { comments, hasMore, loading, posting, postComment, updateComment } =
    useCommentsStore(subject);
  const { dismissMention } = useDismissMention();
  const onSensitiveClick = async (comment: Comment) => {
    const sensitivity: CommentSensitivity =
      comment.data.sensitivity === 'sensitive' ? 'normal' : 'sensitive';
    updateComment({ comment: { id: comment.id, data: { sensitivity } } });
  };

  const allowSensitiveView =
    userAccess?.admin || access?.comment_sensitive_view;
  const filteredComments = allowSensitiveView
    ? comments
    : comments.filter(
        (comment) =>
          comment.data.sensitivity !== 'sensitive' ||
          comment.data.author_id === userId,
      );
  const visibleComments = useMemo(
    () => filteredComments.filter((comment) => !comment.data.deletedAt),
    [filteredComments],
  );

  const allowView = access?.comment_view === 'allow';
  const allowPost = access?.comment_create === 'allow';

  const handleDismissMention = useCallback(async () => {
    if (!currentMention) {
      return false;
    }

    try {
      await dismissMention(currentMention);

      return true;
    } catch (error) {
      console.error('Failed to dismiss reminder:', error);

      return false;
    }
  }, [currentMention, dismissMention]);

  const onCommentInView = useCallback(
    async (comment: Comment) => {
      console.log('In View', comment.id);
      if (currentMention && comment.id) {
        const success = await handleDismissMention();
        if (success) {
          console.log('Dismissed reminder');
        }
      }
    },
    [currentMention, handleDismissMention],
  );

  return (
    <div className='mt-2 flex flex-col md:h-[calc(100vh-21rem)]'>
      <div className='mb-2 flex-none text-large font-semibold'>
        Write a comment
      </div>
      {allowPost && (
        <div className='mt-2 flex-none'>
          <Post
            subject={subject}
            posting={posting}
            postComment={postComment}
            users={mentionUsers}
          />
        </div>
      )}
      {allowView && loading && (
        <div className='mt-6 flex flex-col items-center'>
          <CircularProgress size='lg' />
        </div>
      )}
      {allowView && !loading && (
        <div className='mt-6 flex-grow overflow-y-auto rounded-medium bg-default-100 p-2 md:py-4'>
          <Comments
            comments={visibleComments}
            onSensitiveClick={onSensitiveClick}
            access={access}
            currentComment={target}
            onInView={onCommentInView}
            users={mentionUsers}
            onUpdateComment={updateComment}
          />
        </div>
      )}
      {hasMore && (
        <div>
          <Button>Load More</Button>
        </div>
      )}
    </div>
  );
}

export function Post({
  subject,
  posting,
  postComment,
  className,
  users,
}: {
  subject: Person;
  posting: boolean;
  postComment: ({ comment }: { comment: Comment }) => void;
  className?: string;
  users: User[];
}) {
  const currentUser = useCurrentUser();
  const [urgent, setUrgent] = useState(false);
  const [sensitive, setSensitive] = useState(false);
  const [content, setContent] = useState<SerializedEditorState | null>(null);
  const [empty, setEmpty] = useState(true);
  const clear = useRef<(() => void) | undefined>(undefined);
  if (!currentUser) {
    return undefined;
  }

  if (!subject) {
    return undefined;
  }

  const savePost = async () => {
    if (!content) {
      return;
    }

    const comment = createComment({
      subject,
      currentUser,
      content,
      urgent,
      sensitive,
    });
    postComment({ comment });
    clear.current?.();
    setUrgent(false);
    setSensitive(false);
  };

  const showMyUser = false;

  return (
    <div className={className}>
      <Editor
        users={users}
        placeholder='Say something...'
        content={content}
        onChange={setContent}
        onEmpty={setEmpty}
        clear={clear}
      />
      <div className='mt-2 flex w-full flex-row items-center pl-2'>
        {showMyUser && <UserItem email={false} item={currentUser} />}
        <Spacer className='grow' />
        <Checkbox
          size='sm'
          className='mr-1'
          isSelected={sensitive}
          onValueChange={setSensitive}
        >
          <div className='flex flex-row items-center'>
            Sensitive{' '}
            <StandardIcon id='mdi:alert-circle-outline' className='ml-1' />
          </div>
        </Checkbox>
        <Checkbox
          size='sm'
          className='mr-1'
          isSelected={urgent}
          onValueChange={setUrgent}
        >
          <div className='flex flex-row items-center'>
            Urgent{' '}
            <StandardIcon id='mdi:clock-alert-outline' className='ml-1' />
          </div>
        </Checkbox>
        <Button
          variant='solid'
          color={urgent || sensitive ? 'danger' : 'primary'}
          onPress={savePost}
          isDisabled={empty}
          isLoading={posting}
        >
          <StandardIcon id='mdi:send' color='primary' /> Post
        </Button>
      </div>
    </div>
  );
}

function Comments({
  comments,
  access,
  onSensitiveClick,
  currentComment,
  onInView,
  users,
  onUpdateComment,
}: {
  comments: Comment[];
  access: PeopleAccess;
  onSensitiveClick: (comment: Comment) => Promise<void>;
  currentComment: string | null;
  onInView?: (comment: Comment) => void;
  users: User[];
  onUpdateComment: ({
    comment,
  }: {
    comment: { id: string; data: Partial<Comment['data']> };
  }) => Promise<Comment>;
}) {
  if (!comments.length) {
    return (
      <Empty
        description='No comments'
        icon='mdi:comment-text-multiple-outline'
      />
    );
  }

  return (
    <CommentsListing
      comments={comments}
      access={access}
      onSensitiveClick={onSensitiveClick}
      currentComment={currentComment}
      onInView={onInView}
      users={users}
      onUpdateComment={onUpdateComment}
    />
  );
}

function CommentsListing({
  comments,
  access,
  onSensitiveClick,
  currentComment,
  onInView,
  users,
  onUpdateComment,
}: {
  comments: Comment[];
  access: PeopleAccess;
  onSensitiveClick: (comment: Comment) => Promise<void>;
  currentComment: string | null;
  onInView?: (comment: Comment) => void;
  users: User[];
  onUpdateComment: ({
    comment,
  }: {
    comment: { id: string; data: Partial<Comment['data']> };
  }) => Promise<Comment>;
}) {
  return (
    <div className='flex flex-col gap-4'>
      {comments.map((item) => (
        <CommentDetails
          key={item.id}
          item={item}
          access={access}
          onSensitiveClick={onSensitiveClick}
          isTarget={item.id === currentComment}
          onInView={onInView}
          users={users}
          onUpdateComment={onUpdateComment}
        />
      ))}
    </div>
  );
}

function CommentDetails({
  item,
  access,
  onSensitiveClick,
  isTarget,
  onInView,
  users,
  onUpdateComment,
}: {
  item: Comment;
  access: PeopleAccess;
  onSensitiveClick: (item: Comment) => Promise<void>;
  isTarget: boolean;
  onInView?: (comment: Comment) => void;
  users: User[];
  onUpdateComment: ({
    comment,
  }: {
    comment: { id: string; data: Partial<Comment['data']> };
  }) => Promise<Comment>;
}) {
  const usersLookup = useUsersLookup();
  const userAccess = useUserAccess();
  const userId = useUserId();
  const currentUser = useCurrentUser();
  const author = usersLookup[item.data.author_id];
  const content = item.data.content;
  const allowSensitiveFlag =
    userAccess?.admin || access?.comment_sensitive_flag;
  const time = useMemo(
    () => new Date(item.data.created_at).toLocaleString(),
    [item.data.created_at],
  );
  const currentUserIdentifiers = [
    userId,
    currentUser?.id,
    currentUser?.data.uid,
  ].filter(isNonEmptyString);
  const commentAuthorIdentifiers = [
    item.data.author_id,
    item.data.created_by,
  ].filter(isNonEmptyString);
  const isAuthor = commentAuthorIdentifiers.some((identifier) =>
    currentUserIdentifiers.includes(identifier),
  );
  const canEdit = isAuthor && content.type === 'lexical';
  const canDelete = isAuthor;
  const showActionsMenu = canEdit || canDelete;
  const commentRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState<SerializedEditorState | null>(
    null,
  );
  const [isEditEmpty, setIsEditEmpty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (isTarget && commentRef.current) {
      const scrollContainer = commentRef.current.closest('.overflow-y-auto');
      if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = commentRef.current.getBoundingClientRect();

        const scrollTop =
          elementRect.top -
          containerRect.top +
          scrollContainer.scrollTop -
          containerRect.height / 2 +
          elementRect.height / 2;

        scrollContainer.scrollTo({
          top: scrollTop + 100,
          behavior: 'smooth',
        });
      }
    }
  }, [isTarget]);

  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    if (!isTarget || !commentRef.current) {
      return undefined;
    }

    const scrollContainer = commentRef.current.closest('.overflow-y-auto');

    const observer = new IntersectionObserver(
      ([entry]) => {
        const visibleEnough =
          entry.isIntersecting && entry.intersectionRatio >= 0.2;
        if (visibleEnough && !isInView) {
          onInView?.(item);
          setIsInView(true);
        }
      },
      { root: (scrollContainer as Element) || null, threshold: [0, 0.2, 0.5] },
    );

    observer.observe(commentRef.current);

    return () => observer.disconnect();
  }, [item, onInView, isTarget, isInView]);

  const startEditing = () => {
    if (!canEdit || content.type !== 'lexical' || isEditing) {
      return;
    }

    setEditContent(content.data);
    setIsEditEmpty(false);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    if (isSaving) {
      return;
    }

    setIsEditing(false);
    setEditContent(null);
    setIsEditEmpty(false);
  };

  const handleUpdate = async () => {
    if (!editContent || isEditEmpty || isSaving) {
      return;
    }

    setIsSaving(true);
    try {
      await onUpdateComment({
        comment: {
          id: item.id,
          data: {
            content: {
              type: 'lexical',
              data: editContent,
            },
          },
        },
      });
      setIsEditing(false);
      setEditContent(null);
      setIsEditEmpty(false);
    } catch (error) {
      console.error('Failed to update comment', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete || isDeleting) {
      return;
    }

    setIsDeleting(true);
    try {
      await onUpdateComment({
        comment: {
          id: item.id,
          data: { deletedAt: new Date().toISOString() },
        },
      });
    } catch (error) {
      console.error('Failed to delete comment', error);
      setIsDeleting(false);
    }
  };

  const renderContent = () => {
    if (isEditing && content.type === 'lexical') {
      return (
        <Editor
          users={users}
          content={editContent}
          onChange={setEditContent}
          onEmpty={setIsEditEmpty}
          placeholder='Update your comment...'
          showBorder={false}
        />
      );
    }

    if (content.type === 'lexical') {
      return <CommentContent content={content.data} />;
    }

    return null;
  };

  return (
    <div
      ref={commentRef}
      className={`${isEditing ? '' : 'p-1 md:p-2'} ${
        isTarget ? 'rounded-medium border-2 border-primary-900 bg-white' : ''
      }`.trim()}
    >
      <div className='flex flex-row items-center'>
        {author && (
          <UserItem
            item={author}
            email={false}
            fontWeight='medium'
            fontSize='md'
          />
        )}
        <Spacer className='grow' />
        {item.data.sensitivity !== 'sensitive' && allowSensitiveFlag && (
          <StandardIcon
            id='mdi:alert-circle-outline'
            className='mr-2 cursor-pointer text-gray-300'
            onClick={() => onSensitiveClick(item)}
          />
        )}
        {item.data.sensitivity === 'sensitive' && (
          <StandardIcon
            id='mdi:alert-circle'
            className={`mr-2 text-danger-500 ${allowSensitiveFlag ? 'cursor-pointer' : ''}`}
            onClick={
              allowSensitiveFlag ? () => onSensitiveClick(item) : undefined
            }
          />
        )}
        {item.data.urgency === 'urgent' && (
          <StandardIcon id='mdi:clock-alert' className='mr-2 text-danger-500' />
        )}
        <div className='text-tiny'>{time}</div>
      </div>

      <div
        className={`ml-10 mr-2 text-small text-default-800 ${
          isEditing
            ? 'mt-3 rounded-medium border border-default-200 bg-white shadow-sm'
            : 'mt-2'
        }`}
      >
        {renderContent()}
      </div>
      <div className='ml-10 mr-2 mt-3 flex flex-wrap items-center gap-2'>
        {isEditing ? (
          <div className='flex flex-wrap gap-2'>
            <Button
              variant='light'
              onPress={cancelEditing}
              isDisabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              color='primary'
              onPress={handleUpdate}
              isDisabled={isEditEmpty || isSaving}
              isLoading={isSaving}
            >
              Update
            </Button>
          </div>
        ) : (
          <ReactionBar comment={item} access={access} />
        )}
        {showActionsMenu && <Spacer className='grow' />}
        {showActionsMenu && (
          <CommentActionsMenu
            canEdit={canEdit}
            canDelete={canDelete}
            onEdit={startEditing}
            onDelete={handleDelete}
            disableEdit={isEditing || isSaving}
            isDeleting={isDeleting}
          />
        )}
      </div>
    </div>
  );
}

function isNonEmptyString(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.length > 0;
}

function CommentActionsMenu({
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  disableEdit,
  isDeleting,
}: {
  canEdit: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onDelete: () => void;
  disableEdit?: boolean;
  isDeleting: boolean;
}) {
  if (!canEdit && !canDelete) {
    return null;
  }

  return (
    <Dropdown placement='bottom-end'>
      <DropdownTrigger>
        <Button
          isIconOnly
          size='sm'
          variant='light'
          className='mr-2'
          aria-label='Comment actions'
        >
          <StandardIcon id='mdi:dots-vertical' />
        </Button>
      </DropdownTrigger>
      <DropdownMenu aria-label='Comment actions'>
        {canEdit && (
          <DropdownItem key='edit' onPress={onEdit} isDisabled={disableEdit}>
            Edit
          </DropdownItem>
        )}
        {canDelete && (
          <DropdownItem
            key='delete'
            color='danger'
            className='text-danger-500'
            onPress={onDelete}
            isDisabled={isDeleting}
          >
            Delete
          </DropdownItem>
        )}
      </DropdownMenu>
    </Dropdown>
  );
}

function CommentContent({ content }: { content: SerializedEditorState }) {
  return <Display content={content} />;
}

const REACTION_EMOJI: Record<ReactionType, string> = {
  fire: '🔥',
  smile: '😄',
  heart: '❤️',
  thumbs_up: '👍',
  pray: '🙏',
  tada: '🎉',
  raised_hands: '🙌',
  clap: '👏',
};

function useReactionStats(
  reactions: ReactionsMap,
  userId: string | null | undefined,
) {
  return useMemo(() => {
    const counts: Partial<Record<ReactionType, number>> = {};
    const active: Partial<Record<ReactionType, boolean>> = {};
    const firstSeen: Partial<Record<ReactionType, string>> = {};

    for (const [uid, userMap] of Object.entries(reactions)) {
      for (const [t, ts] of Object.entries(userMap || {})) {
        const rt = t as ReactionType;
        const timestamp = ts as string;
        counts[rt] = (counts[rt] || 0) + 1;

        if (uid === userId) {
          active[rt] = true;
        }

        if (!firstSeen[rt] || timestamp < firstSeen[rt]) {
          firstSeen[rt] = timestamp;
        }
      }
    }

    const sortedTypes = (Object.keys(counts) as ReactionType[]).sort((a, b) => {
      const timeA = firstSeen[a] || '';
      const timeB = firstSeen[b] || '';

      return timeA.localeCompare(timeB);
    });

    return { counts, active, sortedTypes };
  }, [reactions, userId]);
}

function ReactionBar({
  comment,
  access,
}: {
  comment: Comment;
  access: PeopleAccess;
}) {
  const { api, auth } = useApiWithAuth();
  const userId = useUserId();
  const [localReactions, setLocalReactions] = useState<
    ReactionsMap | undefined
  >(comment.data.reactions);
  const [togglingTypes, setTogglingTypes] = useState<Set<ReactionType>>(
    new Set(),
  );
  const canReact = access?.comment_create === 'allow';
  const commentIdRef = useRef(comment.id);
  const justUpdatedRef = useRef(false);

  useEffect(() => {
    if (justUpdatedRef.current) {
      justUpdatedRef.current = false;

      return;
    }

    if (comment.id !== commentIdRef.current) {
      commentIdRef.current = comment.id;

      setLocalReactions(comment.data.reactions);
    }
  }, [comment]);

  const reactions = (localReactions || {}) as ReactionsMap;
  const { counts, active, sortedTypes } = useReactionStats(reactions, userId);

  const toggle = async (type: ReactionType) => {
    if (!userId || !canReact || togglingTypes.has(type)) {
      return;
    }

    setTogglingTypes((prev) => new Set(prev).add(type));

    setLocalReactions((prevReactions) => {
      const currentReactions = { ...(prevReactions || {}) } as ReactionsMap;
      const userReactions = { ...(currentReactions[userId] || {}) } as Record<
        string,
        string
      >;
      const hasReaction = Boolean(userReactions[type]);

      if (hasReaction) {
        delete userReactions[type];
      } else {
        userReactions[type] = new Date().toISOString();
      }

      const nextReactions = { ...currentReactions };
      if (Object.keys(userReactions).length > 0) {
        nextReactions[userId] = userReactions;
      } else {
        delete nextReactions[userId];
      }

      return nextReactions;
    });

    try {
      const updated = await apiReactToComment({
        api,
        auth,
        id: comment.id,
        userId,
        type,
        action: 'toggle',
      });

      justUpdatedRef.current = true;
      setLocalReactions(updated.data.reactions);
    } catch (e) {
      console.error('Failed to react to comment', e);

      setLocalReactions(comment.data.reactions);
    } finally {
      setTogglingTypes((prev) => {
        const next = new Set(prev);
        next.delete(type);

        return next;
      });
    }
  };

  const chips = sortedTypes.map((t) => (
    <ReactionChip
      key={t}
      type={t}
      label={REACTION_EMOJI[t]}
      count={counts[t] || 0}
      active={Boolean(active[t])}
      disabled={!canReact}
      onClick={() => toggle(t)}
      users={reactions}
    />
  ));

  return (
    <div className='flex flex-row items-center gap-2'>
      {chips}
      <ReactionPicker disabled={!canReact} onSelect={toggle} />
    </div>
  );
}

function ReactionChip({
  type,
  label,
  count,
  active,
  disabled,
  onClick,
  users,
}: {
  type: ReactionType;
  label: string;
  count: number;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
  users: ReactionsMap;
}) {
  const lookup = useUsersLookup();
  const entries: { name: string; time: string }[] = [];
  for (const [uid, map] of Object.entries(users)) {
    if (map?.[type]) {
      const u = lookup[uid];
      const name = u?.data?.displayName || 'Unknown';
      const time = new Date(map[type] as string).toLocaleString();
      entries.push({ name, time });
    }
  }

  entries.sort((a, b) => a.name.localeCompare(b.name));

  const [open, setOpen] = useState(false);
  const longPressRef = useRef<number | null>(null);
  const hoverTimeoutRef = useRef<number | null>(null);
  const longPressTriggeredRef = useRef(false);

  const handleMouseEnter = () => {
    // Show popup after a short delay on hover
    if (hoverTimeoutRef.current) {
      window.clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = window.setTimeout(() => setOpen(true), 300);
  };

  const handleMouseLeave = () => {
    // Clear hover timeout if leaving before popup shows
    if (hoverTimeoutRef.current) {
      window.clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    setOpen(false);
  };

  const handleClick = () => {
    // Don't toggle if long press was triggered
    if (longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false;

      return;
    }

    onClick();
  };

  const content = (
    <div className='min-w-48 max-w-sm p-2'>
      <div className='mb-2 text-xs font-semibold text-default-600'>
        {label} Reactions
      </div>
      <div className='flex flex-col gap-2'>
        {entries.map((e, i) => (
          <div key={i} className='flex flex-col gap-0.5'>
            <div className='text-sm font-medium text-default-900'>{e.name}</div>
            <div className='text-xs text-default-500'>{e.time}</div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Popover isOpen={open} onOpenChange={setOpen} placement='top' offset={8}>
      <PopoverTrigger>
        <button
          type='button'
          disabled={disabled}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={`flex h-8 items-center rounded-full border px-2 text-small ${active ? 'border-primary-300 bg-primary-100' : 'border-default-300 bg-default-100'} ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
          onContextMenu={(e) => {
            e.preventDefault();
            setOpen(true);
          }}
          onTouchStart={() => {
            longPressTriggeredRef.current = false;

            if (longPressRef.current) {
              window.clearTimeout(longPressRef.current);
            }

            // open after ~450ms long-press
            longPressRef.current = window.setTimeout(() => {
              longPressTriggeredRef.current = true;
              setOpen(true);
            }, 450);
          }}
          onTouchEnd={() => {
            if (longPressRef.current) {
              window.clearTimeout(longPressRef.current);
              longPressRef.current = null;
            }
          }}
          aria-pressed={active}
          aria-label={`Reaction ${label} count ${count}`}
        >
          <span className='mr-1 text-base'>{label}</span>
          <span className='text-xs'>{count}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent>{content}</PopoverContent>
    </Popover>
  );
}

const REACTION_TYPES: ReactionType[] = [
  'thumbs_up',
  'smile',
  'heart',
  'fire',
  'tada',
  'raised_hands',
  'clap',
  'pray',
];

function ReactionPicker({
  disabled,
  onSelect,
}: {
  disabled: boolean;
  onSelect: (t: ReactionType) => void;
}) {
  const [open, setOpen] = useState(false);
  const [positionAbove, setPositionAbove] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleOpen = () => {
    if (!buttonRef.current) {
      return;
    }

    // Find the scrollable comments container
    const scrollContainer = buttonRef.current.closest('.overflow-y-auto');
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const menuHeight = 100; // Approximate height of the menu

    if (scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const spaceBelow = containerRect.bottom - buttonRect.bottom;
      setPositionAbove(spaceBelow < menuHeight);
    } else {
      // Fallback to viewport if no scroll container found
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      setPositionAbove(spaceBelow < menuHeight);
    }

    setOpen(true);
  };

  return (
    <div className='relative'>
      <button
        ref={buttonRef}
        type='button'
        disabled={disabled}
        className={`flex h-8 items-center rounded-full border px-2 text-small ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
        onClick={handleOpen}
        aria-haspopup='menu'
        aria-expanded={open}
        aria-label='Add reaction'
        title='Add reaction'
      >
        <StandardIcon id='material-symbols:add-reaction-outline' />
      </button>
      {open && (
        <div
          role='menu'
          className={`absolute z-10 flex w-56 flex-wrap gap-2 rounded-medium border bg-white p-2 shadow-md dark:bg-default-900 ${
            positionAbove ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
          onMouseLeave={() => setOpen(false)}
        >
          {REACTION_TYPES.map((type) => (
            <button
              key={type}
              type='button'
              className='rounded-md border px-2 py-1 text-lg hover:bg-default-100'
              onClick={() => {
                onSelect(type);
                setOpen(false);
              }}
            >
              {REACTION_EMOJI[type]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function sortByCreatedAtDesc(a: Comment, b: Comment) {
  if (a.data.created_at > b.data.created_at) {
    return -1;
  }

  if (a.data.created_at < b.data.created_at) {
    return 1;
  }

  return 0;
}

function prepareComments(list: Comment[]) {
  return list
    .filter((comment) => !comment.data.deletedAt)
    .sort(sortByCreatedAtDesc);
}

function useCommentsStore(subject: Person) {
  const { api, auth } = useApiWithAuth();
  const loaded = useRef(false);
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [posting, setPosting] = useState(false);
  useEffect(() => {
    if (loaded.current || !subject) {
      return;
    }

    loaded.current = true;
    setLoading(true);
    loadComments({ api, auth, subject })
      .then((data) => {
        setComments(prepareComments(data));
        setLoading(false);

        return undefined;
      })
      .catch((e) => {
        console.error(e);
        setLoading(false);
      });
  }, [api, auth, subject]);
  const postComment = ({ comment }: { comment: Comment }) => {
    setPosting(true);

    return createPost({ api, auth, comment })
      .then((result) => {
        setComments((prev) => prepareComments([result, ...prev]));

        return undefined;
      })
      .finally(() => {
        setPosting(false);
      });
  };

  const updateComment = async ({
    comment,
  }: {
    comment: { id: string; data: Partial<Comment['data']> };
  }) => {
    const result = await updatePost({ api, auth, comment });
    setComments((prev) => {
      const remaining = prev.filter((item) => item.id !== comment.id);
      if (result.data.deletedAt) {
        return remaining;
      }

      return prepareComments([...remaining, result]);
    });

    return result;
  };

  // TODO paging, hasMore, loadMore
  return {
    loading,
    comments,
    hasMore: false,
    postComment,
    posting,
    updateComment,
  };
}

function createComment({
  subject,
  currentUser,
  content,
  urgent,
  sensitive,
}: {
  subject: Person;
  currentUser: User;
  content: SerializedEditorState;
  urgent: boolean;
  sensitive: boolean;
}) {
  const mentions = Array.from(findMentions(content.root, new Set<string>()));
  const data: Comment = {
    id: '',
    data: {
      subject_id: subject.id,
      author_id: currentUser?.id || '',
      urgency: urgent ? 'urgent' : 'normal',
      sensitivity: sensitive ? 'sensitive' : 'normal',
      context: {
        id: '',
        type: '',
      },
      content: {
        type: 'lexical',
        data: content,
      },
      created_at: '',
      created_by: currentUser?.id || '',
      mentions,
    },
  };

  return data;
}

function isParentNode(
  node: SerializedLexicalNode,
): node is SerializedLexicalNode & { children: SerializedLexicalNode[] } {
  return 'children' in node && Array.isArray(node.children);
}

function isMentionNode(
  node: SerializedLexicalNode,
): node is SerializedLexicalNode & SerializedBeautifulMentionNode {
  return node.type === 'beautifulMention';
}

function isUserMentionNode(
  node: SerializedLexicalNode,
): node is SerializedLexicalNode &
  SerializedBeautifulMentionNode &
  UserMentionNode {
  if (isMentionNode(node)) {
    return typeof node.data?.id === 'string';
  }

  return false;
}

type UserMentionNode = SerializedBeautifulMentionNode & {
  data: {
    id: string;
  };
};

function findMentions(node: SerializedLexicalNode, out: Set<string>) {
  if (isUserMentionNode(node)) {
    out.add(node.data.id);
  } else if (isParentNode(node)) {
    for (const child of node.children) {
      findMentions(child, out);
    }
  }

  return out;
}
